/**
 * Unread notification count for LeftSidebar / BottomTabBar badges.
 * Refreshes on socket `notification`, decrements on CustomEvent `notifications:read`.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/auth.reducer';
import { useSocket } from './SocketContext';

const NotificationCountContext = createContext({
  unreadCount: 0,
  refreshUnreadCount: async () => {},
  bumpUnread: () => {},
});

export function NotificationCountProvider({ children }) {
  const user = useSelector(selectUser);
  const userId = user?.user_id ?? null;
  const { subscribe, connected } = useSocket();
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnreadCount = useCallback(async () => {
    if (!userId) {
      setUnreadCount(0);
      return;
    }
    try {
      const res = await fetch('/api/notifications/unread-count', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(typeof data.count === 'number' ? data.count : 0);
      }
    } catch {
      /* ignore */
    }
  }, [userId]);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  useEffect(() => {
    const onRead = () => {
      setUnreadCount((c) => Math.max(0, c - 1));
    };
    window.addEventListener('notifications:read', onRead);
    return () => window.removeEventListener('notifications:read', onRead);
  }, []);

  useEffect(() => {
    if (!connected || !userId) return;
    return subscribe('notification', () => {
      refreshUnreadCount();
    });
  }, [connected, userId, subscribe, refreshUnreadCount]);

  const bumpUnread = useCallback(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  const value = {
    unreadCount,
    refreshUnreadCount,
    bumpUnread,
  };

  return (
    <NotificationCountContext.Provider value={value}>
      {children}
    </NotificationCountContext.Provider>
  );
}

export function useNotificationCount() {
  return useContext(NotificationCountContext);
}
